import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { ScrollText, Globe, Timer, MapPin, ArrowRight } from "lucide-react";
import Walkthrough from "../components/Walkthrough.jsx";
import RateNudge from "../components/RateNudge.jsx";
import { getSessionCount, wasRateNudgeDismissed, hasBeenPromptedToRate } from "../lib/appFeedback";

const SUBJECT_IMAGES = {
  history: "https://images.pexels.com/photos/32874843/pexels-photo-32874843.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  geography: "https://images.pexels.com/photos/7861519/pexels-photo-7861519.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [syllabus, setSyllabus] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/syllabus").then((r) => setSyllabus(r.data));
    api.get("/profile/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  const firstName = user?.name?.split(" ")[0] || "there";
  const chaptersDone = progress?.chapters?.filter((c) => c.mcqs_done).length || 0;
  const totalChapters = progress?.total_chapters || 0;
  const lastMock = progress?.mocks?.[0];
  const showRateNudge =
    getSessionCount() >= 5 && !wasRateNudgeDismissed() && !hasBeenPromptedToRate();

  return (
    <div className="space-y-10">
      <Walkthrough />
      <section className="stagger-in">
        <p className="text-sm text-brimi-navyText/60 uppercase tracking-widest font-semibold">Welcome back</p>
        <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight text-brimi-navyText mt-1">
          Hi {firstName} 👋
        </h1>
        <p className="text-brimi-navyText/75 mt-2">Pick a subject and class to dive in.</p>
      </section>

      <section className="grid lg:grid-cols-3 gap-5">
        <div className="brimi-card p-6 lg:col-span-2 flex items-center gap-5">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60">Chapter progress</p>
            <p className="text-3xl font-heading font-bold text-brimi-navyText mt-1">
              {chaptersDone}<span className="text-brimi-navyText/50 text-xl">/{totalChapters}</span>
            </p>
            <div className="h-2 mt-3 rounded-full bg-brimi-creamSoft overflow-hidden">
              <div className="h-full bg-brimi-navy transition-all" style={{ width: `${totalChapters ? (chaptersDone / totalChapters) * 100 : 0}%` }} />
            </div>
          </div>
          <Link to="/mock-test" className="brimi-btn-primary" data-testid="dashboard-mock-cta">
            <Timer size={16} /> Take Mock
          </Link>
        </div>
        <Link to="/map-work" className="brimi-card p-6 flex items-center gap-4 group" data-testid="dashboard-map-cta">
          <div className="w-12 h-12 rounded-xl grid place-items-center bg-brimi-sage/15 text-brimi-sage">
            <MapPin size={22} />
          </div>
          <div className="flex-1">
            <p className="font-heading font-semibold text-brimi-navyText">India Map Work</p>
            <p className="text-xs text-brimi-navyText/65">Tap-to-place quiz</p>
          </div>
          <ArrowRight size={18} className="text-brimi-navyText/40 group-hover:translate-x-0.5 transition" />
        </Link>
      </section>

      {syllabus &&
        Object.entries(syllabus).map(([key, subj]) => (
          <section key={key}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-brimi-navyText flex items-center gap-3">
                {key === "history" ? <ScrollText size={22} className="text-brimi-terracotta" /> : <Globe size={22} className="text-brimi-sage" />}
                {subj.name}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {Object.keys(subj.classes).map((cls) => (
                <Link
                  to={`/subject/${key}/${cls}`}
                  key={cls}
                  data-testid={`subject-card-${key}-${cls}`}
                  className="brimi-card p-6 overflow-hidden relative group"
                  style={{
                    borderColor:
                      key === "history" ? "rgba(226,114,91,0.3)" : "rgba(138,154,91,0.3)",
                  }}
                >
                  <div className="absolute inset-0 opacity-[0.07]" aria-hidden>
                    <img src={SUBJECT_IMAGES[key]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="relative">
                    <span
                      className={`brimi-pill ${
                        key === "history"
                          ? "bg-brimi-terracotta/15 text-brimi-terracotta"
                          : "bg-brimi-sage/15 text-brimi-sage"
                      }`}
                    >
                      Class {cls === "9" ? "IX" : "X"}
                    </span>
                    <h3 className="font-heading font-semibold text-xl text-brimi-navyText mt-3">
                      {subj.name} · Class {cls === "9" ? "IX" : "X"}
                    </h3>
                    <p className="text-sm text-brimi-navyText/70 mt-1">
                      {subj.classes[cls].length} chapters · ICSE 2028 syllabus
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 font-semibold text-brimi-navy group-hover:gap-3 transition-all">
                      Start studying <ArrowRight size={16} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}

      {lastMock && (
        <section className="brimi-card p-6">
          <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60">Last mock test</p>
          <p className="text-2xl font-heading font-semibold text-brimi-navyText mt-1">
            {lastMock.score}/{lastMock.total} · {Math.round((lastMock.score / lastMock.total) * 100)}%
          </p>
        </section>
      )}

      {showRateNudge && <RateNudge />}
    </div>
  );
}
